/**
 * Global custom context menu — replaces the browser native context menu
 * everywhere in the app. Offers universal text actions (copy, cut, paste,
 * select all) and a refresh entry. Specific elements can extend the menu
 * by tagging themselves with `data-ctx-actions` (see registerActions /
 * useContextActions below).
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ClipboardText, Scissors, ClipboardText as Paste, ArrowsClockwise, Selection, PencilSimple, Trash, ArrowUp, ArrowDown, Copy } from '@phosphor-icons/react';
import { toast } from 'sonner';

// ---------------------------------------------------------------------------
// Action registry — components can register custom actions for a specific
// element via the `useContextActions` hook. The hook returns a ref callback
// that should be applied to the wrapping DOM node. When the user right-clicks
// inside that node, the registered actions will appear in the menu.
// ---------------------------------------------------------------------------
const ACTION_REGISTRY = new Map(); // domNode -> [{label, icon, onClick, disabled}]

export const useContextActions = (actions) => {
  const ref = useRef(null);
  useEffect(() => {
    const node = ref.current;
    if (!node) return undefined;
    if (actions && actions.length > 0) {
      ACTION_REGISTRY.set(node, actions);
    } else {
      ACTION_REGISTRY.delete(node);
    }
    return () => ACTION_REGISTRY.delete(node);
  }, [actions]);
  return ref;
};

const collectCustomActions = (target) => {
  // Walk up the DOM tree and merge custom actions from every parent node
  // that has registered actions. Closest node wins for duplicates.
  let node = target;
  const seenLabels = new Set();
  const out = [];
  while (node && node !== document.body) {
    const acts = ACTION_REGISTRY.get(node);
    if (acts) {
      for (const a of acts) {
        if (!seenLabels.has(a.label)) {
          seenLabels.add(a.label);
          out.push(a);
        }
      }
    }
    node = node.parentNode;
  }
  return out;
};

// ---------------------------------------------------------------------------
// Provider — wraps the app, listens to contextmenu globally
// ---------------------------------------------------------------------------
export const ContextMenuProvider = ({ children }) => {
  const [menu, setMenu] = useState(null);

  const onContextMenu = useCallback((e) => {
    // Shift+RightClick keeps the native browser menu (for developers).
    if (e.shiftKey) return;
    // Allow native menu on file inputs (otherwise the user can't pick files).
    const tag = (e.target?.tagName || '').toLowerCase();
    if (tag === 'input' && (e.target.type === 'file')) return;
    e.preventDefault();
    const isEditable = (tag === 'input' || tag === 'textarea' || e.target?.isContentEditable);
    const selectionText = window.getSelection()?.toString() || '';
    const customActions = collectCustomActions(e.target);
    setMenu({
      x: e.clientX,
      y: e.clientY,
      target: e.target,
      isEditable,
      hasSelection: selectionText.length > 0,
      customActions,
    });
  }, []);

  useEffect(() => {
    document.addEventListener('contextmenu', onContextMenu);
    return () => document.removeEventListener('contextmenu', onContextMenu);
  }, [onContextMenu]);

  // Close menu on click / scroll / esc anywhere
  useEffect(() => {
    if (!menu) return undefined;
    const close = () => setMenu(null);
    const onKey = (e) => { if (e.key === 'Escape') close(); };
    window.addEventListener('click', close);
    window.addEventListener('scroll', close, true);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('click', close);
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('keydown', onKey);
    };
  }, [menu]);

  return (
    <>
      {children}
      {menu && <ContextMenu state={menu} onClose={() => setMenu(null)} />}
    </>
  );
};

// ---------------------------------------------------------------------------
// Menu UI
// ---------------------------------------------------------------------------
const ContextMenu = ({ state, onClose }) => {
  const { x, y, target, isEditable, hasSelection, customActions } = state;
  const ref = useRef(null);
  const [pos, setPos] = useState({ left: x, top: y });

  // Re-position to keep menu within viewport
  useEffect(() => {
    if (!ref.current) return;
    const r = ref.current.getBoundingClientRect();
    let left = x;
    let top = y;
    if (left + r.width > window.innerWidth - 8) left = window.innerWidth - r.width - 8;
    if (top + r.height > window.innerHeight - 8) top = window.innerHeight - r.height - 8;
    setPos({ left, top });
  }, [x, y]);

  const doCopy = async () => {
    try {
      const sel = window.getSelection()?.toString() || '';
      if (sel) await navigator.clipboard.writeText(sel);
      else if (isEditable && typeof target.value === 'string') await navigator.clipboard.writeText(target.value);
    } catch (e) { toast.error('Copie impossible'); }
  };
  const doCut = async () => {
    try {
      const sel = window.getSelection()?.toString() || '';
      if (sel) {
        await navigator.clipboard.writeText(sel);
        if (isEditable) {
          // Replace the current selection inside the input
          document.execCommand('delete');
        } else {
          window.getSelection()?.deleteFromDocument?.();
        }
      }
    } catch (e) { toast.error('Couper impossible'); }
  };
  const doPaste = async () => {
    try {
      const txt = await navigator.clipboard.readText();
      if (!txt) return;
      if (isEditable && target?.focus) {
        target.focus();
        document.execCommand('insertText', false, txt);
      } else {
        await navigator.clipboard.writeText(txt); // re-stage clipboard
        toast.info('Cliquez d\'abord dans un champ texte avant de coller');
      }
    } catch (e) { toast.error('Collage impossible (autorisation refusee par le navigateur)'); }
  };
  const doSelectAll = () => {
    if (isEditable && target.select) target.select();
    else {
      const range = document.createRange();
      range.selectNodeContents(target);
      const sel = window.getSelection();
      sel?.removeAllRanges();
      sel?.addRange(range);
    }
  };
  const doRefresh = () => window.location.reload();

  const Sep = () => <div style={{ height: 1, background: '#E5E7EB', margin: '4px 0' }} />;
  const Item = ({ icon: Icon, label, shortcut, onClick, disabled }) => (
    <button
      type="button"
      data-testid={`ctx-${label.replace(/\s+/g, '-').toLowerCase()}`}
      onClick={() => { if (!disabled) { onClick(); onClose(); } }}
      disabled={disabled}
      style={{
        display: 'flex', alignItems: 'center', gap: 8, width: '100%', textAlign: 'left',
        padding: '6px 14px', border: 'none', background: 'transparent', cursor: disabled ? 'not-allowed' : 'pointer',
        fontSize: '0.85rem', color: disabled ? '#C5C7CD' : '#1F2024', whiteSpace: 'nowrap',
      }}
      onMouseEnter={(e) => { if (!disabled) e.currentTarget.style.background = '#F4F5F7'; }}
      onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
    >
      {Icon && <Icon size={15} />}
      <span style={{ flex: 1 }}>{label}</span>
      {shortcut && <span style={{ color: '#9E9EA7', fontSize: '0.74rem', marginLeft: 16 }}>{shortcut}</span>}
    </button>
  );

  return (
    <div
      ref={ref}
      data-testid="app-context-menu"
      style={{
        position: 'fixed', left: pos.left, top: pos.top, zIndex: 99999,
        background: 'white', border: '1px solid #E2E2E6', borderRadius: 6,
        boxShadow: '0 8px 24px rgba(0,0,0,0.18)', minWidth: 220, padding: '4px 0',
        fontFamily: 'inherit', userSelect: 'none',
      }}
      onClick={(e) => e.stopPropagation()}
      onContextMenu={(e) => { e.preventDefault(); e.stopPropagation(); }}
    >
      <Item icon={ClipboardText} label="Copier" shortcut="Ctrl+C" onClick={doCopy} disabled={!hasSelection && !(isEditable && target?.value)} />
      <Item icon={Scissors} label="Couper" shortcut="Ctrl+X" onClick={doCut} disabled={!hasSelection || !isEditable} />
      <Item icon={Paste} label="Coller" shortcut="Ctrl+V" onClick={doPaste} disabled={!isEditable} />
      <Item icon={Selection} label="Tout selectionner" shortcut="Ctrl+A" onClick={doSelectAll} />
      {customActions && customActions.length > 0 && (
        <>
          <Sep />
          {customActions.map((a, i) => (
            <Item key={`ctx-custom-${a.label}`} icon={a.icon || null} label={a.label} onClick={a.onClick} disabled={a.disabled} />
          ))}
        </>
      )}
      <Sep />
      <Item icon={ArrowsClockwise} label="Rafraichir la page" shortcut="F5" onClick={doRefresh} />
    </div>
  );
};

// Convenience helpers to keep call sites concise
export const CtxIcons = { Edit: PencilSimple, Delete: Trash, Up: ArrowUp, Down: ArrowDown, Copy };

export default ContextMenuProvider;
